<template>
  <div
    :class="{ 'has-error': errors.any() }"
    class="amount-variable-input"
  >
    <div class="reward-amount">
      <v-text-field
        v-validate="validationRules"
        :value="value"
        class="mb-2"
        type="number"
        solo
        flat
        hide-details
        name="input-reward-amount"
        :aria-label="amountSelectedAriaLabel"
        role="textbox"
        contenteditable="true"
        @input="$emit('input', $event)"
      >
        <template #prepend>
          <div>
            {{ currencyCode }}
          </div>
        </template>
      </v-text-field>
      <label
        class="range-label fs14"
        tabindex="0"
      >
        {{ `(` + variableAmountString + `)` }}
      </label>
    </div>
    <div
      v-if="hasErrors(errors)"
      class="error-block mt-1"
      tabindex="0"
    >
      <validation-tip
        id="input-reward-amount-error-required"
        :error-bag="errors"
        rule="required"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount"
        aria-live="polite"
      />
      <validation-tip
        id="input-reward-amount-error-min"
        :error-bag="errors"
        rule="min_value"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount-min"
        aria-live="polite"
      />
      <validation-tip
        id="input-reward-amount-error-max"
        :error-bag="errors"
        rule="max_value"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount-max"
        aria-live="polite"
      />
      <validation-tip
        id="input-reward-amount-error-integer"
        :error-bag="errors"
        rule="integer"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount-integer"
        aria-live="polite"
      />
      <validation-tip
        id="input-reward-amount-error-valid-amount"
        :error-bag="errors"
        rule="remainingBalance"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount-balance"
        aria-live="polite"
      />
      <validation-tip
        id="input-reward-amount-error-valid-amount-with-fee"
        :error-bag="errors"
        rule="feeCheck"
        control="input-reward-amount"
        i18n-key="reward-variable-valid-amount-balance-with-fee"
        aria-live="polite"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { GET_REWARD_LINK_BALANCE } from '@/store/get-types'
import ValidationTip from '../../shared/forms/ValidationTip.vue'
import { get } from 'lodash'

export default {
  name: 'AmountVariableInput',
  components: {
    ValidationTip
  },
  inject: ['$validator'],
  props: {
    value: {
      type: String,
      required: false,
      default: null
    },
    item: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    cachedItem: {}
  }),
  computed: {
    ...mapGetters({
      availableBalance: GET_REWARD_LINK_BALANCE
    }),
    currencyCode () {
      const { cachedItem: { currencyCode } = {} } = this
      return currencyCode
    },
    maxValue () {
      const { cachedItem: { maxValue } = {} } = this
      return maxValue
    },
    minValue () {
      const { cachedItem: { minValue } = {} } = this
      return minValue
    },
    validationRules () {
      const { cachedItem = {}, availableBalance } = this
      const { isWholeAmountValueRequired, minValue, maxValue } = cachedItem

      return {
        required: true,
        regex: /^\d*(\.\d{1,2})?$/,
        min_value: +minValue,
        max_value: +maxValue,
        integer: isWholeAmountValueRequired,
        remainingBalance: [availableBalance],
        feeCheck: [availableBalance, cachedItem]
      }
    },
    variableAmountString () {
      return (
        this.$t('reward-variable-from-amount') +
        ` ${this.$money(this.minValue, this.currencyCode)} ` +
        this.$t('reward-variable-to-amount') +
        ` ${this.$money(this.maxValue, this.currencyCode)}`
      )
    },
    amountSelectedAriaLabel () {
      return (
        this.$t('enter-amount') +
        ' ' +
        this.variableAmountString +
        ', ' +
        this.$t('numeric-only')
      )
    }
  },
  watch: {
    item: function (oldItem, newItem) {
      if (oldItem.utid !== newItem.utid) {
        this.cachedItem = JSON.parse(JSON.stringify(newItem))
      }
    }
  },
  mounted () {
    this.cachedItem = JSON.parse(JSON.stringify(this.item))
  },
  methods: {
    hasErrors (errors) {
      return get(errors, 'items.length') > 0
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../assets/css/variables';
@import '~vuetify/src/styles/styles';

.amount-variable-input {
  width: 100%;
}

::v-deep .reward-amount {
  .v-input__prepend-outer {
    height: 48px;
    min-width: 80px;
    background-color: #fff;
    border-right: 1px solid map-get($blue-grey, 'darken-2');
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    color: black;
  }

  .v-input {
    border: 1px solid map-get($blue-grey, 'darken-2');

    input {
      text-align: center;
      font-size: 20px;
      &[type='number'] {
        -moz-appearance: textfield;
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }
  }
}

.list-leave-active {
  .range-label {
    display: none;
    font-size: $font-size-md;
    color: $tangoTextPrimary;
  }
}

.range-label {
  color: $tangoTextPrimary;
}
</style>
